/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback } from "react";
import { secureRandomString } from "matrix-js-sdk/lib/randomstring";

import { useClient } from "../ClientContext";
import { useInteractiveRegistration } from "../auth/useInteractiveRegistration";
import { generateRandomName } from "../auth/generateRandomName";
import { useRecaptcha } from "../auth/useRecaptcha";
import { widget } from "../widget";
import { callTraceJournal, describeTraceError } from "../telemetry";

interface UseRegisterPasswordlessUserType {
  privacyPolicyUrl?: string;
  registerPasswordlessUser: (displayName: string) => Promise<void>;
  recaptchaId?: string;
}

export function useRegisterPasswordlessUser(): UseRegisterPasswordlessUserType {
  const { setClient } = useClient();
  const { privacyPolicyUrl, recaptchaKey, register } =
    useInteractiveRegistration();
  const { execute, reset, recaptchaId } = useRecaptcha(recaptchaKey);

  const registerPasswordlessUser = useCallback(
    async (displayName: string) => {
      if (!setClient) {
        throw new Error("No client context");
      }
      if (widget) {
        throw new Error(
          "Registration was skipped: We should never try to register password-less user in embedded mode.",
        );
      }

      const startedAt = performance.now();
      const relatedEventId = callTraceJournal.record({
        eventType: "auth.guest_registration_started",
        stage: "auth",
        outcome: "started",
      });
      let operation:
        | "passwordless_recaptcha"
        | "passwordless_registration"
        | "passwordless_client_setup" = "passwordless_recaptcha";
      try {
        const recaptchaResponse = await execute();
        const userName = generateRandomName();
        operation = "passwordless_registration";
        const [client, session] = await register(
          userName,
          secureRandomString(16),
          displayName,
          recaptchaResponse,
          true,
        );
        operation = "passwordless_client_setup";
        setClient(client, session);
        callTraceJournal.setIdentity(
          client.getUserId(),
          client.getDeviceId() ?? null,
        );
        callTraceJournal.record({
          eventType: "auth.guest_registration_succeeded",
          stage: "auth",
          outcome: "succeeded",
          relatedEventId,
          durationMs: Math.round(performance.now() - startedAt),
        });
      } catch (e) {
        callTraceJournal.record({
          eventType: "auth.guest_registration_failed",
          stage: "auth",
          severity: "error",
          outcome: "failed",
          reasonCode: "guest_registration_failed",
          payload: { operation, ...describeTraceError(e) },
        });
        reset();
        throw e;
      }
    },
    [execute, reset, register, setClient],
  );

  return { privacyPolicyUrl, registerPasswordlessUser, recaptchaId };
}
